import React, { useState, useEffect, useMemo } from "react";
import { CircularProgress, makeStyles, IconButton } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import { formatCurrency, formatMessage } from "lib/globalize-util";
import { createProductRecord } from "helpers/product-helpers";
import App from "lib/app";
import me from "models/me";
import theme from "styles/theme";
import Cart from "components/icons/cart";
import Image from "components/image/image";
import { updateProductsForInsights } from "actions/cart-legacy";
import type { IAddLineItemPayload } from "types/working-order/line-items";
import type { IState as IScope } from "reducers/scope";
import type { ProductStyleInsights } from "models/insights/product-style-insights";
import { InsightsActions } from "constants/analytics";
import { useSetOrderFlowType } from "models/new/smart-orders";
import { FLOW_TYPE } from "constants/order-types";

const app = App;

const useStyles = makeStyles<
    Theme,
    {
        placeOrderDisabled: boolean;
    }
>({
    root: ({ placeOrderDisabled }) => ({
        paddingTop: "20px",
        display: "inline-flex",
        width: "100%",
        fontSize: "1.3rem",
        cursor: placeOrderDisabled ? "wait" : "pointer",
    }),
    clickContainer: {
        width: "calc(100% - 50px)",
        display: "inline-flex",
    },
    rank: {
        fontWeight: "bold",
        width: "25px",
    },
    rankContainer: {
        width: "25px",
    },
    image: {
        width: "95px",
    },
    imageContainer: {
        position: "relative",
        width: "95px",
        marginRight: "5px",
    },
    productName: {
        fontWeight: "bold",
    },
    price: {
        fontWeight: "bold",
        display: "inline-flex",
    },
    text: {
        margin: "0 0 4px",
    },
    priceType: {
        fontSize: "1.1rem",
        margin: "0 0 4px",
    },
    cart: {
        position: "relative",
        top: "-12px",
        right: 0,
    },
    cartContainer: {
        marginLeft: "auto",
    },
});

interface InsightsProductProps {
    product: ProductStyleInsights;
    rank: number;
    scope: IScope;
    cart: any;
    onAddLineItem: (item: IAddLineItemPayload, fromInsightsWoPanel: boolean) => void;
    onRemoveProductFromCart: (productId: string) => void;
    onClick: () => void;
    isCartPage?: boolean;
    placeOrderDisabled: boolean;
    setPlaceOrderDisabled: (value: boolean) => void;
    productType?: string;
}

const nuorderFallbackImage = "//cdn1.nuorder.com/brands/1womenswholesale_missing.jpg";

const ALLOWED_CURRENCIES = ["USD"];

const InsightsProduct: React.FC<InsightsProductProps> = ({
    product,
    rank,
    scope,
    cart,
    onAddLineItem,
    onRemoveProductFromCart,
    onClick,
    isCartPage,
    placeOrderDisabled,
    setPlaceOrderDisabled,
    productType,
}) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [productInCart, setProductInCart] = useState(false);

    useEffect(() => {
        setProductInCart(cart.items.has(product.product_id));
    }, [cart.items]);

    const classes = useStyles({ placeOrderDisabled });

    const handleClickOnProduct = () => {
        global.analytics.track(InsightsActions.productClick);
        onClick();
    };
    const setOrderFlowAPI = useSetOrderFlowType();

    const handlePlaceOrder = async () => {
        // @ts-ignore - there is some missing properties in the product object
        // but they don't seem to be actually required to remove an item
        const item = createProductRecord(Object.assign({}, product, { _id: product.product_id }));

        const params = {
            body: {
                item_id: product.product_id,
                type: "order_trends",
            },
        };

        if (cart) {
            if (cart.items.has(product.product_id)) {
                if (isCartPage) {
                    onRemoveProductFromCart(product.product_id);
                    app.legacy.sendMessage(updateProductsForInsights(item._id));
                } else {
                    app.trigger("remove-from-cart", {
                        item,
                    });

                    me.trackAction("Remove from order", {
                        selectAll: false,
                        bulk: false,
                        resource: "insights-panel",
                    });
                }
            } else {
                setShowSpinner(true);

                const order = cart.entities?.orders[cart.result];
                const orderFlowType = order && order.order_flow_type;
                const flowType = productType === "immediate" ? FLOW_TYPE.immediate : FLOW_TYPE.prebook;
                if (scope.brand.hasPreference("new_order_flow") && !orderFlowType) {
                    await setOrderFlowAPI.setOrderFlowType({ orderId: cart.result, flowType });
                }

                onAddLineItem(params, isCartPage);

                global.analytics.track(InsightsActions.addProductToCartClick, {
                    "Product ID": product.product_id,
                });

                setProductInCart(true);
                setTimeout(() => {
                    setShowSpinner(false);
                }, 1000);
            }

            setPlaceOrderDisabled(true);
            setTimeout(() => {
                setPlaceOrderDisabled(false);
            }, 1000);
        }
    };

    const wholesalePrice = useMemo(() => {
        const pricing = product.pricing.filter(
            (pricing) => pricing.disabled === false && ALLOWED_CURRENCIES.includes(pricing.currency_code)
        )[0];

        return pricing ? formatCurrency(pricing.wholesale, pricing.currency_code) : null;
    }, [product.pricing]);

    const msrpPrice = useMemo(() => {
        const pricing = product.pricing.filter(
            (pricing) => pricing.disabled === false && ALLOWED_CURRENCIES.includes(pricing.currency_code)
        )[0];
        return pricing ? formatCurrency(pricing.retail, pricing.currency_code) : null;
    }, [product.pricing]);

    const hideMsrp = scope.hideMsrp;
    const hideWholesale = scope.hideWholesale;

    return (
        <div className={classes.root}>
            <div className={classes.clickContainer} onClick={handleClickOnProduct}>
                <div className={classes.rankContainer}>
                    <div className={classes.rank}>#{rank}</div>
                </div>
                <div className={classes.imageContainer}>
                    <Image className={classes.image} src={[product.getImageUrl(scope), nuorderFallbackImage]} />
                </div>
                <div>
                    <div className={classes.productName}>{product.name}</div>
                    <div className={classes.text}>{product.style_number}</div>
                    <div className={classes.text}>{product.color}</div>
                    <div className={classes.text}>{product.category}</div>
                    {wholesalePrice && !hideWholesale && (
                        <div className={classes.priceType}>
                            {formatMessage("Wholesale")} <span className={classes.price}>{wholesalePrice}</span>
                        </div>
                    )}
                    {msrpPrice && !hideMsrp && (
                        <div className={classes.priceType}>
                            {formatMessage("MSRP")} <span className={classes.price}>{msrpPrice}</span>
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.cartContainer}>
                <IconButton
                    aria-label="shopping-cart"
                    disabled={placeOrderDisabled || showSpinner}
                    onClick={(e) => {
                        // stop event propagation to prevent the whole row from being clicked
                        e.stopPropagation();
                        if (placeOrderDisabled || showSpinner) {
                            return;
                        }
                        handlePlaceOrder();
                    }}
                    className={classes.cart}
                >
                    {showSpinner && <CircularProgress size={16} thickness={1} />}
                    {!showSpinner && (
                        <Cart
                            color={productInCart ? theme.palette.primary1Color : theme.palette.darkGrey}
                            hoverColor={theme.palette.darkerGrey}
                            aria-label={
                                productInCart
                                    ? formatMessage("Remove from Cart ") + product.product_id
                                    : formatMessage("Add to Cart ") + product.product_id
                            }
                        />
                    )}
                </IconButton>
            </div>
        </div>
    );
};

export const TopStyleInsightsProduct = InsightsProduct;
